/**
 * Season date maths for One Harvest Season.
 *
 * Business days skip weekends and the hard pause bands (Thanksgiving, the holidays). The light
 * band around Dec 14–18 is a caution, not a stop, so it is drawn but never skipped.
 */
export const CHICAGO = "America/Chicago";

export const toDate = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

export const toIso = (date) => date.toISOString().slice(0, 10);

export const addDays = (date, days) => {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

export const isWeekend = (date) => date.getUTCDay() === 0 || date.getUTCDay() === 6;

export const inPause = (date, pauses, { includeLight = false } = {}) =>
  (pauses || []).some((p) => {
    if (p.light && !includeLight) return false;
    return date >= toDate(p.start) && date <= toDate(p.end);
  });

export const pauseLabel = (date, pauses) => {
  const hit = (pauses || []).find(
    (p) => date >= toDate(p.start) && date <= toDate(p.end)
  );
  if (!hit) return null;
  return `${formatShort(hit.start)} – ${formatShort(hit.end)}${hit.light ? " (light week)" : ""}`;
};

/** Advance `count` working days from `start`, skipping weekends and hard pauses. */
export const businessDaysAfter = (start, count, pauses) => {
  let day = new Date(start.getTime());
  let moved = 0;
  while (moved < count) {
    day = addDays(day, 1);
    if (isWeekend(day) || inPause(day, pauses)) continue;
    moved += 1;
  }
  return day;
};

/**
 * Step date from an enrollment date. `business_days` steps walk working days; the raw
 * calendar date is returned alongside so the desk can flag where a plain day count would land.
 */
export const stepDate = (enrollIso, step, pauses) => {
  const enroll = toDate(enrollIso);
  const offset = Math.max(0, (step.day_offset || 1) - 1);
  const raw = addDays(enroll, offset);
  const business = step.business_days === false ? raw : businessDaysAfter(enroll, offset, pauses);
  const issues = [];
  if (isWeekend(raw)) issues.push(raw.getUTCDay() === 6 ? "raw date is a Saturday" : "raw date is a Sunday");
  const rawPause = pauseLabel(raw, pauses);
  if (rawPause && inPause(raw, pauses)) issues.push(`raw date falls in the pause ${rawPause}`);
  else if (rawPause) issues.push(`raw date falls in the light week ${rawPause}`);
  return { raw, date: business, issues, shifted: toIso(raw) !== toIso(business) };
};

export const formatShort = (iso) =>
  toDate(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export const formatLong = (date) =>
  date.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });

export const formatStamp = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: CHICAGO,
      })
    : "—";

export const currentPhase = (phases, today = new Date()) => {
  const day = toDate(today.toISOString());
  const active = (phases || []).find((p) => day >= toDate(p.start) && day <= toDate(p.end));
  if (active) {
    const left = Math.ceil((toDate(active.end) - day) / 86400000);
    return { ...active, daysLeft: Math.max(0, left), state: "active" };
  }
  const next = (phases || []).find((p) => toDate(p.start) > day);
  if (next) {
    const until = Math.ceil((toDate(next.start) - day) / 86400000);
    return { ...next, daysLeft: until, state: "upcoming" };
  }
  return { name: "Season closed", daysLeft: 0, state: "done" };
};
